import React, { useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore
import Plotly from 'plotly.js-dist-min';

type DiseaseId = 'FMD' | 'LSD' | 'SGP' | 'PPR';
type SpeciesId = 'cattle' | 'sheep' | 'goat' | 'buffalo';

const DISEASES: { id: DiseaseId; label: string }[] = [
  { id: 'FMD', label: 'FMD' },
  { id: 'LSD', label: 'LSD' },
  { id: 'SGP', label: 'SPGP' },
  { id: 'PPR', label: 'PPR' },
];

/** Species relevant to clinical surveillance per disease */
const DISEASE_SPECIES: Record<DiseaseId, { id: SpeciesId; label: string; census: string; exam: string; pos: string }[]> = {
  FMD: [
    { id: 'cattle', label: 'Cattle', census: 'cattle', exam: 'cattle_exam', pos: 'cattle_pos_fmd' },
    { id: 'sheep', label: 'Sheep', census: 'sheep', exam: 'sheep_exam', pos: 'sheep_pos_fmd' },
    { id: 'goat', label: 'Goats', census: 'goat', exam: 'goat_exam', pos: 'goat_pos_fmd' },
    { id: 'buffalo', label: 'Buffaloes', census: 'buffalo', exam: 'buffalo_exam', pos: 'buffalo_pos_fmd' },
  ],
  LSD: [
    { id: 'cattle', label: 'Cattle', census: 'cattle', exam: 'cattle_exam', pos: 'cattle_pos_lsd' },
    { id: 'buffalo', label: 'Buffaloes', census: 'buffalo', exam: 'buffalo_exam', pos: 'buffalo_pos_lsd' },
  ],
  SGP: [
    { id: 'sheep', label: 'Sheep', census: 'sheep', exam: 'sheep_exam', pos: 'sheep_pos_sgp' },
    { id: 'goat', label: 'Goats', census: 'goat', exam: 'goat_exam', pos: 'goat_pos_sgp' },
  ],
  PPR: [
    { id: 'sheep', label: 'Sheep', census: 'sheep', exam: 'sheep_exam', pos: 'sheep_pos_ppr' },
    { id: 'goat', label: 'Goats', census: 'goat', exam: 'goat_exam', pos: 'goat_pos_ppr' },
  ],
};

const COLORS = {
  positive: '#f5b4b4',
  examinedNeg: '#a8d4ef',
  notExamined: '#e5e7eb',
};

/** Min display width (%) so small Positive / Examined shares stay visible */
const MIN_SEGMENT_DISPLAY_PCT = 2.5;

/**
 * Widen Positive and Examined when > 0 but tiny; take the extra from the grey
 * (not examined) segment first so the bar still sums to ~100%.
 */
function displayStackedPcts(
  posTrue: number,
  examTrue: number,
  notTrue: number,
): { pos: number; exam: number; not: number } {
  const pos = posTrue > 0 ? Math.max(posTrue, MIN_SEGMENT_DISPLAY_PCT) : 0;
  const exam = examTrue > 0 ? Math.max(examTrue, MIN_SEGMENT_DISPLAY_PCT) : 0;
  const need = pos - posTrue + (exam - examTrue);
  let not = Math.max(0, notTrue - need);
  let sum = pos + exam + not;
  if (sum > 100 && pos + exam > 0) {
    // Grey couldn't absorb the boost — scale active segments into 100%
    const scale = 100 / (pos + exam);
    return { pos: pos * scale, exam: exam * scale, not: 0 };
  }
  // Pad leftover into grey so axis reads as full population
  if (sum < 100) not += 100 - sum;
  return { pos, exam, not };
}

function n(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0;
  const x = Number(v);
  return Number.isFinite(x) && x > 0 ? x : 0;
}

interface NestRow {
  label: string;
  area: string;
  species: string;
  census: number;
  exam: number;
  positive: number;
  examinedNeg: number;
  notExamined: number;
}

function buildNestRows(clinical: any[], disease: DiseaseId): NestRow[] {
  const specs = DISEASE_SPECIES[disease];
  const out: NestRow[] = [];
  for (const row of clinical || []) {
    const area = String(row.district_province || row.district || '—');
    for (const sp of specs) {
      const census = n(row[sp.census]);
      let exam = n(row[sp.exam]);
      let positive = n(row[sp.pos]);
      // Keep nested parts inside the whole
      if (exam > census && census > 0) exam = census;
      if (positive > exam) positive = exam;
      if (census <= 0 && exam <= 0 && positive <= 0) continue;
      const total = Math.max(census, exam, positive);
      const examCap = Math.min(exam, total);
      const posCap = Math.min(positive, examCap);
      out.push({
        label: `${area} · ${sp.label}`,
        area,
        species: sp.label,
        census: total,
        exam: examCap,
        positive: posCap,
        examinedNeg: Math.max(0, examCap - posCap),
        notExamined: Math.max(0, total - examCap),
      });
    }
  }
  return out;
}

export interface CycleClinicalVizProps {
  clinical: any[];
  quarter: number;
  year: number;
}

const CycleClinicalViz: React.FC<CycleClinicalVizProps> = ({ clinical, quarter, year }) => {
  const [disease, setDisease] = useState<DiseaseId>('FMD');
  const chartRef = useRef<HTMLDivElement | null>(null);

  const nestRows = useMemo(() => buildNestRows(clinical, disease), [clinical, disease]);
  // Only rows with at least one clinical exam — empty bars aren't useful
  const chartRows = useMemo(() => nestRows.filter((r) => r.exam > 0), [nestRows]);
  const diseaseLabel = DISEASES.find((d) => d.id === disease)?.label || disease;

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    if (!chartRows.length) {
      Plotly.purge(el);
      return;
    }

    // Unique y keys (Plotly needs uniqueness); tick labels omit repeated district names
    const yKeys = chartRows.map((_, i) => String(i));
    let prevArea: string | null = null;
    const yLabels = chartRows.map((r) => {
      if (r.area !== prevArea) {
        prevArea = r.area;
        return `${r.area} · ${r.species}`;
      }
      return r.species;
    });

    const pct = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0);
    const posTrue = chartRows.map((r) => pct(r.positive, r.census));
    const examTrue = chartRows.map((r) => pct(r.examinedNeg, r.census));
    const notTrue = chartRows.map((r) => pct(r.notExamined, r.census));
    const display = posTrue.map((p, i) => displayStackedPcts(p, examTrue[i], notTrue[i]));
    const posPct = display.map((d) => d.pos);
    const examPct = display.map((d) => d.exam);
    const notPct = display.map((d) => d.not);
    const examTotalPct = chartRows.map((r) => pct(r.exam, r.census));

    const fmtPct = (displayV: number, trueV: number) => {
      if (trueV <= 0 && displayV <= 0) return '';
      if (trueV > 0 && trueV < 3) return `${trueV < 1 ? trueV.toFixed(1) : trueV.toFixed(0)}%`;
      return displayV >= 3 ? `${trueV.toFixed(0)}%` : '';
    };

    const hoverBase = chartRows.map((r, i) => {
      const posOfExam = r.exam > 0 ? ((r.positive / r.exam) * 100).toFixed(1) : '—';
      return (
        `<b>${r.area}</b> · ${r.species}<br>` +
        `Total animals in epiunits visited this quarter: ${r.census.toLocaleString()} (100%)<br>` +
        `Clinically examined: ${r.exam.toLocaleString()} (${examTotalPct[i].toFixed(1)}%)<br>` +
        `Clinically positive: ${r.positive.toLocaleString()} (${posTrue[i].toFixed(1)}%; ${posOfExam}% of examined)<br>` +
        `Examined, not positive: ${r.examinedNeg.toLocaleString()} (${examTrue[i].toFixed(1)}%)<br>` +
        `Not examined: ${r.notExamined.toLocaleString()} (${notTrue[i].toFixed(1)}%)`
      );
    });

    const traces = [
      {
        type: 'bar',
        orientation: 'h',
        name: 'Clinically positive',
        y: yKeys,
        x: posPct,
        marker: { color: COLORS.positive, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: posPct.map((v, i) => fmtPct(v, posTrue[i])),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#374151' },
      },
      {
        type: 'bar',
        orientation: 'h',
        name: 'Examined, not positive',
        y: yKeys,
        x: examPct,
        marker: { color: COLORS.examinedNeg, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: examPct.map((v, i) => fmtPct(v, examTrue[i])),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#374151' },
      },
      {
        type: 'bar',
        orientation: 'h',
        name: 'Not examined (of animals in visited epiunits)',
        y: yKeys,
        x: notPct,
        marker: { color: COLORS.notExamined, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: notPct.map((v, i) => fmtPct(v, notTrue[i])),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#6b7280' },
      },
    ];

    const rowH = 28;
    const height = Math.max(280, 80 + chartRows.length * rowH);
    const maxLabelLen = yLabels.reduce((m, s) => Math.max(m, String(s).length), 0);
    // Room for long district/province · species ticks (first letter was clipped at l:140)
    const leftMargin = Math.max(180, Math.min(360, Math.ceil(maxLabelLen * 7.2) + 24));

    Plotly.newPlot(
      el,
      traces,
      {
        barmode: 'stack',
        margin: { t: 16, r: 24, b: 48, l: leftMargin },
        height,
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { size: 12, color: '#374151' },
        xaxis: {
          title: {
            text: '% of total animals in epiunits visited this quarter',
            font: { size: 12 },
          },
          range: [0, 100],
          ticksuffix: '%',
          gridcolor: '#f3f4f6',
          zeroline: false,
        },
        yaxis: {
          autorange: 'reversed',
          automargin: true,
          ticklabeloverflow: 'allow',
          tickmode: 'array',
          tickvals: yKeys,
          ticktext: yLabels,
          tickfont: { size: 11 },
        },
        legend: {
          orientation: 'h',
          y: -0.12,
          x: 0,
          font: { size: 11 },
        },
        bargap: 0.25,
      },
      { responsive: true, displayModeBar: false },
    );

    return () => {
      try {
        Plotly.purge(el);
      } catch {
        /* ignore */
      }
    };
  }, [chartRows, disease]);

  return (
    <div className="space-y-4">
      <h3 className="text-2xl font-black text-gray-800 bg-red-100 px-4 py-3 rounded">
        Clinical Examination — Quarter {quarter}, {year}
      </h3>

      <p className="text-sm text-gray-600 leading-relaxed max-w-3xl">
        Each bar is 100% of total animals in epiunits visited this quarter (not the regional census).
        Coloured segments show clinically positive, examined but not positive, and not examined.
        Small shares are widened slightly so they stay visible; hover shows the true %.
      </p>

      <div className="flex flex-wrap gap-2">
        {DISEASES.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setDisease(d.id)}
            className={`nav-btn px-4 py-1.5 text-sm ${
              disease === d.id ? 'ring-2 ring-green-600 ring-offset-1' : 'opacity-80'
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3">
        {chartRows.length ? (
          <div ref={chartRef} style={{ width: '100%' }} />
        ) : (
          <p className="py-10 text-center text-sm text-gray-600 leading-relaxed max-w-xl mx-auto">
            {nestRows.length
              ? `No animals were clinically examined for ${diseaseLabel} in this quarter.`
              : `No clinical examination data for ${diseaseLabel} in this quarter.`}
          </p>
        )}
      </div>
    </div>
  );
};

export default CycleClinicalViz;
